/**
 * PAGE-MCH-USER-008 代理专属开户向导（公开）。
 * 路由 /onboard?invite=；无 JWT；四步仅 UI，一次 POST 全字段。
 */
import { mapApiError } from '@/lib/apiErrorMessages';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Plus, Store, Trash2, Upload } from 'lucide-react';
import { IDENTITY_ACCEPT, validateCredentialFile } from '@/components/CredentialPreview';
import { useToast } from '@/components/Toast';
import {
  INVITE_GONE_COPY,
  inviteGoneCopy,
  inviteTokenFromSearch,
  previewPublicAgentInvite,
  submitPublicAgentInvite,
  uploadPublicInviteFile,
  type PublicProductOption,
  type PublicSubmitResult,
  type PublicUboItem,
} from '@/api/publicAgentInvite';

const STEPS = ['企业信息', '执照与法人', '产品与受益人', '预览提交'] as const;

const ID_TYPES = [
  { value: 'ID_CARD', label: '身份证' },
  { value: 'PASSPORT', label: '护照' },
  { value: 'HK_MACAO_PERMIT', label: '港澳通行证' },
  { value: 'OTHER', label: '其他' },
] as const;

const CONTROL_TYPES = [
  { value: 'OWNERSHIP', label: '所有权' },
  { value: 'CONTROL', label: '控制权' },
  { value: 'BOTH', label: '所有权+控制权' },
] as const;

const NATIONS = [
  { value: 'CN', label: 'CN - 中国大陆' },
  { value: 'HK', label: 'HK - 中国香港' },
  { value: 'MO', label: 'MO - 中国澳门' },
  { value: 'SG', label: 'SG - 新加坡' },
  { value: 'US', label: 'US - 美国' },
  { value: 'GB', label: 'GB - 英国' },
] as const;

const LOGIN_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const OWNERSHIP_RE = /^(?:100(?:\.0{1,2})?|\d{1,2}(?:\.\d{1,2})?)$/;

type UploadedSlot = {
  fileId: string;
  fileName: string;
  previewUrl: string;
};

type UboRow = {
  key: number;
  uboName: string;
  uboNationality: string;
  uboIdType: string;
  uboIdNo: string;
  ownershipPercentage: string;
  controlType: string;
  isPep: boolean;
  pepDetails: string;
  uboBirthday: string;
  uboAddress: string;
  uboAddressCountry: string;
  file: UploadedSlot | null;
};

let uboSeq = 0;

function emptyUboRow(): UboRow {
  return {
    key: ++uboSeq,
    uboName: '',
    uboNationality: 'CN',
    uboIdType: 'ID_CARD',
    uboIdNo: '',
    ownershipPercentage: '',
    controlType: '',
    isPep: false,
    pepDetails: '',
    uboBirthday: '',
    uboAddress: '',
    uboAddressCountry: 'CN',
    file: null,
  };
}

function idTypeLabel(value: string): string {
  return ID_TYPES.find((t) => t.value === value)?.label || value;
}

function cardTypeLabel(value: string): string {
  if (value === 'VIRTUAL') {
    return '虚拟卡';
  }
  if (value === 'PHYSICAL') {
    return '实体卡';
  }
  return value;
}

function maskSecret(value: string): string {
  const s = value.trim();
  if (!s) {
    return '—';
  }
  if (s.length <= 4) {
    return '****';
  }
  return `${s.slice(0, 2)}****${s.slice(-2)}`;
}

function isOwnershipAtLeast25(raw: string): boolean {
  const v = raw.trim();
  if (!v || !OWNERSHIP_RE.test(v)) {
    return false;
  }
  const whole = v.split('.')[0] || '0';
  const wholeN = Number.parseInt(whole, 10);
  return Number.isFinite(wholeN) && wholeN >= 25;
}

function isValidUboRow(row: UboRow): boolean {
  if (!row.uboName.trim() || !row.uboIdType.trim() || !row.uboIdNo.trim()) {
    return false;
  }
  const control = row.controlType.trim().toUpperCase();
  return control === 'CONTROL' || isOwnershipAtLeast25(row.ownershipPercentage);
}

function toUboItem(row: UboRow): PublicUboItem {
  return {
    uboName: row.uboName.trim(),
    uboNationality: row.uboNationality.trim() || undefined,
    uboIdType: row.uboIdType.trim() || undefined,
    uboIdNo: row.uboIdNo.trim() || undefined,
    uboIdFiles: row.file ? [row.file.fileId] : undefined,
    uboBirthday: row.uboBirthday.trim() || undefined,
    uboAddress: row.uboAddress.trim() || undefined,
    uboAddressCountry: row.uboAddressCountry.trim() || undefined,
    ownershipPercentage: row.ownershipPercentage.trim() || undefined,
    controlType: row.controlType.trim() || undefined,
    isPoliticallyExposedPerson: row.isPep,
    pepDetails: row.isPep && row.pepDetails.trim() ? row.pepDetails.trim() : undefined,
  };
}

function PublicSlotUpload({
  token,
  label,
  value,
  disabled,
  onChange,
  onGone,
}: {
  token: string;
  label: string;
  value: UploadedSlot | null;
  disabled?: boolean;
  onChange: (next: UploadedSlot | null) => void;
  onGone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');

  const onPick = async (file?: File) => {
    if (!file || disabled) {
      return;
    }
    const err = validateCredentialFile(file, 'IDENTITY');
    if (err) {
      setLocalError(err);
      return;
    }
    setBusy(true);
    setLocalError('');
    const res = await uploadPublicInviteFile(token, file, 'IDENTITY');
    setBusy(false);
    if (!res.ok) {
      if (res.code === '3211') {
        onGone();
        return;
      }
      setLocalError(res.error || '上传失败');
      return;
    }
    if (!res.data.fileId) {
      setLocalError('上传失败，未返回文件');
      return;
    }
    if (value?.previewUrl) {
      URL.revokeObjectURL(value.previewUrl);
    }
    onChange({
      fileId: res.data.fileId,
      fileName: file.name,
      previewUrl: URL.createObjectURL(file),
    });
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] text-slate-400 mb-2">{label}</p>
          <img
            src={value.previewUrl}
            alt={label}
            className="max-h-40 mx-auto rounded object-contain"
          />
          <p className="text-[11px] text-slate-500 mt-2 truncate">{value.fileName}</p>
        </div>
      ) : (
        <p className="text-xs text-slate-500">{label}</p>
      )}
      <label
        className={`relative flex items-center gap-2 input cursor-pointer overflow-hidden ${
          disabled || busy ? 'opacity-60' : ''
        }`}
      >
        <Upload className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="text-sm text-slate-600 truncate">
          {busy ? '上传中…' : value ? '更换影像' : '选择文件'}
        </span>
        <input
          type="file"
          accept={IDENTITY_ACCEPT}
          className="absolute inset-0 z-[1] h-full w-full cursor-pointer opacity-0"
          aria-label={value ? '更换影像' : '选择文件'}
          disabled={disabled || busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            void onPick(file);
          }}
        />
      </label>
      <p className="text-[11px] text-slate-400">仅 jpg / png / webp，单文件 ≤10MB</p>
      {localError ? <p className="text-xs text-red-500">{localError}</p> : null}
    </div>
  );
}

export default function OnboardPage() {
  const toast = useToast();
  const location = useLocation();
  const token = inviteTokenFromSearch(location.search);

  const [loading, setLoading] = useState(() => Boolean(token));
  const [previewCode, setPreviewCode] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [agentDisplayName, setAgentDisplayName] = useState('');
  const [products, setProducts] = useState<PublicProductOption[]>([]);
  const [goneOverride, setGoneOverride] = useState(false);

  const [step, setStep] = useState(0);
  const [legalName, setLegalName] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [registrationNo, setRegistrationNo] = useState('');
  const [license, setLicense] = useState<UploadedSlot | null>(null);
  const [legalPersonName, setLegalPersonName] = useState('');
  const [legalPersonIdType, setLegalPersonIdType] = useState('ID_CARD');
  const [legalPersonIdNo, setLegalPersonIdNo] = useState('');
  const [legalFiles, setLegalFiles] = useState<UploadedSlot[]>([]);
  const [intendedProducts, setIntendedProducts] = useState<string[]>([]);
  const [uboRows, setUboRows] = useState<UboRow[]>(() => [emptyUboRow()]);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<PublicSubmitResult | null>(null);

  const goneCopy = goneOverride
    ? INVITE_GONE_COPY
    : inviteGoneCopy(token, previewCode);

  const loadPreview = useCallback(async (invite: string) => {
    setLoading(true);
    setPreviewError(null);
    setPreviewCode(null);
    const res = await previewPublicAgentInvite(invite);
    if (!res.ok) {
      setPreviewCode(res.code || null);
      setPreviewError(res.error || '预检失败');
      setProducts([]);
      setLoading(false);
      return;
    }
    setAgentDisplayName(res.data.agentDisplayName);
    setProducts(res.data.products);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    void loadPreview(token);
  }, [token, loadPreview]);

  useEffect(() => {
    return () => {
      if (license?.previewUrl) {
        URL.revokeObjectURL(license.previewUrl);
      }
      legalFiles.forEach((f) => URL.revokeObjectURL(f.previewUrl));
      uboRows.forEach((r) => {
        if (r.file?.previewUrl) {
          URL.revokeObjectURL(r.file.previewUrl);
        }
      });
    };
    // 仅卸载时释放本地预览
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allowedProductIds = useMemo(
    () => new Set(products.map((p) => p.productId)),
    [products],
  );

  const markGone = () => {
    setGoneOverride(true);
  };

  const validateStep = (index: number): string | null => {
    if (index === 0) {
      const name = legalName.trim();
      if (name.length < 2 || name.length > 200) {
        return '法定名称须为 2–200 字';
      }
      if (!LOGIN_EMAIL_RE.test(loginEmail.trim())) {
        return '请填写有效登录邮箱';
      }
      if (!registrationNo.trim()) {
        return '请填写注册号';
      }
      return null;
    }
    if (index === 1) {
      if (!license?.fileId) {
        return '请上传营业执照';
      }
      if (!legalPersonName.trim()) {
        return '请填写法人姓名';
      }
      if (!legalPersonIdType.trim()) {
        return '请选择法人证件类型';
      }
      if (!legalPersonIdNo.trim()) {
        return '请填写法人证件号';
      }
      if (legalFiles.length < 1) {
        return '请至少上传 1 张法人证件影像';
      }
      return null;
    }
    if (index === 2) {
      const selected = intendedProducts.filter((id) => allowedProductIds.has(id));
      if (selected.length < 1) {
        return products.length === 0
          ? '须先向平台申请产品授权'
          : '请至少勾选 1 个意向产品';
      }
      for (const row of uboRows) {
        const pct = row.ownershipPercentage.trim();
        if (pct && !OWNERSHIP_RE.test(pct)) {
          return 'UBO 持股比例须为 0–100（最多两位小数）';
        }
        if (isOwnershipAtLeast25(pct) && !row.controlType.trim()) {
          return `UBO「${row.uboName.trim() || '未命名'}」持股 ≥25% 时控制类型必填`;
        }
      }
      if (!uboRows.some(isValidUboRow)) {
        return '请至少登记 1 条有效最终受益人（姓名+证件类型+号码，且持股≥25% 或控制权）';
      }
      return null;
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      setFormError(err);
      toast.error(err);
      return;
    }
    setFormError(null);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const onSubmit = async () => {
    if (!token || submitting) {
      return;
    }
    for (let i = 0; i <= 2; i += 1) {
      const err = validateStep(i);
      if (err) {
        setStep(i);
        setFormError(err);
        toast.error(err);
        return;
      }
    }
    const selected = intendedProducts.filter((id) => allowedProductIds.has(id));
    setSubmitting(true);
    setFormError(null);
    const res = await submitPublicAgentInvite(token, {
      legalName: legalName.trim(),
      loginEmail: loginEmail.trim(),
      registrationNo: registrationNo.trim(),
      licenseFileId: license?.fileId || '',
      legalPersonName: legalPersonName.trim(),
      legalPersonIdType: legalPersonIdType.trim(),
      legalPersonIdNo: legalPersonIdNo.trim(),
      legalPersonIdFiles: legalFiles.map((f) => f.fileId),
      intendedProducts: selected,
      uboList: uboRows.filter(isValidUboRow).map(toUboItem),
    });
    setSubmitting(false);
    if (!res.ok) {
      if (res.code === '3211') {
        markGone();
        return;
      }
      const failMsg = mapApiError(res.code, res.error || '提交失败');
      setFormError(failMsg);
      toast.error(failMsg);
      return;
    }
    setResult(res.data);
    toast.success('申请已提交');
  };

  const shell = (body: ReactNode) => (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 bg-brand-600 rounded-xl flex items-center justify-center">
            <Store className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">商户开户</h1>
            <p className="text-xs text-slate-500">凭代理专属链接提交开户资料</p>
          </div>
        </div>
        {body}
      </div>
    </div>
  );

  if (!token || goneCopy) {
    return shell(
      <div className="card p-10 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <p className="text-base font-semibold text-slate-800">{INVITE_GONE_COPY}</p>
        <p className="text-xs text-slate-500 mt-2">请联系邀请方重新获取开户链接</p>
      </div>,
    );
  }

  if (loading) {
    return shell(
      <div className="card p-10 text-center text-sm text-slate-400">正在校验开户链接…</div>,
    );
  }

  if (previewError) {
    return shell(
      <div className="card p-8 text-center space-y-3">
        <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
        <p className="text-sm font-medium text-slate-700">{previewError}</p>
        <button type="button" className="btn-secondary" onClick={() => token && void loadPreview(token)}>
          重试
        </button>
      </div>,
    );
  }

  if (result) {
    const pending = result.status === 'PENDING_APPROVAL';
    return shell(
      <div className="card p-10 text-center space-y-3">
        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
        <p className="text-base font-semibold text-slate-800">
          {pending ? '申请已提交，等待平台审核' : '申请已受理'}
        </p>
        <p className="text-xs text-slate-500">
          {result.legalName || legalName.trim()}
          {result.status ? ` · ${result.status}` : ''}
        </p>
        {result.status === 'PENDING_RESUBMIT' ? (
          <p className="text-xs text-amber-700">资料待补件，请使用同一链接继续完善后再次提交</p>
        ) : (
          <p className="text-xs text-slate-400">审核通过前不会开通登录账号</p>
        )}
      </div>,
    );
  }

  return shell(
    <div className="card p-6 space-y-6">
      {agentDisplayName ? (
        <p className="text-sm text-slate-600">
          邀请方：<span className="font-medium text-slate-900">{agentDisplayName}</span>
        </p>
      ) : null}

      <ol className="grid grid-cols-4 gap-2">
        {STEPS.map((label, i) => (
          <li
            key={label}
            className={`text-center rounded-lg px-2 py-2 text-[11px] font-medium ${
              i === step
                ? 'bg-brand-50 text-brand-700'
                : i < step
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-slate-100 text-slate-500'
            }`}
          >
            {i + 1}. {label}
          </li>
        ))}
      </ol>

      {formError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {formError}
        </div>
      ) : null}

      {step === 0 && (
        <div className="space-y-4">
          <label className="block">
            <span className="label">法定名称 *</span>
            <input
              className="input"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="与执照一致的企业法定名称"
            />
          </label>
          <label className="block">
            <span className="label">登录邮箱 *</span>
            <input
              className="input"
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              placeholder="开户通过后用于登录"
              autoComplete="email"
            />
          </label>
          <label className="block">
            <span className="label">注册号 *</span>
            <input
              className="input"
              value={registrationNo}
              onChange={(e) => setRegistrationNo(e.target.value)}
              placeholder="统一社会信用代码 / 注册号"
            />
          </label>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <PublicSlotUpload
            token={token}
            label="营业执照"
            value={license}
            onChange={setLicense}
            onGone={markGone}
          />
          <label className="block">
            <span className="label">法人姓名 *</span>
            <input
              className="input"
              value={legalPersonName}
              onChange={(e) => setLegalPersonName(e.target.value)}
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="label">法人证件类型 *</span>
              <select
                className="input"
                value={legalPersonIdType}
                onChange={(e) => setLegalPersonIdType(e.target.value)}
              >
                {ID_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="label">法人证件号 *</span>
              <input
                className="input font-mono"
                value={legalPersonIdNo}
                onChange={(e) => setLegalPersonIdNo(e.target.value)}
                autoComplete="off"
              />
            </label>
          </div>
          <div className="space-y-3">
            <p className="label">法人证件影像 *（1～2 张）</p>
            {legalFiles.map((file, i) => (
              <div key={file.fileId} className="relative">
                <PublicSlotUpload
                  token={token}
                  label={i === 0 ? '法人证件影像' : `法人证件影像 ${i + 1}`}
                  value={file}
                  onChange={(next) => {
                    if (!next) {
                      setLegalFiles((prev) => prev.filter((_, j) => j !== i));
                      return;
                    }
                    setLegalFiles((prev) => prev.map((x, j) => (j === i ? next : x)));
                  }}
                  onGone={markGone}
                />
              </div>
            ))}
            {legalFiles.length < 2 ? (
              <PublicSlotUpload
                token={token}
                label={legalFiles.length === 0 ? '法人证件影像' : '添加法人证件'}
                value={null}
                onChange={(next) => {
                  if (next) {
                    setLegalFiles((prev) => [...prev, next]);
                  }
                }}
                onGone={markGone}
              />
            ) : null}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div>
            <p className="label mb-2">意向产品 *（仅展示该代理已授权产品）</p>
            {products.length === 0 ? (
              <p className="text-xs text-amber-700">须先向平台申请产品授权</p>
            ) : (
              <div className="space-y-2">
                {products.map((p) => (
                  <label key={p.productId} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={intendedProducts.includes(p.productId)}
                      onChange={(e) => {
                        setIntendedProducts((prev) =>
                          e.target.checked
                            ? [...prev, p.productId]
                            : prev.filter((id) => id !== p.productId),
                        );
                      }}
                    />
                    <span>
                      {p.productName || p.productId}
                      {p.cardType ? ` · ${cardTypeLabel(p.cardType)}` : ''}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="label mb-0">最终受益人（UBO）*</p>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => setUboRows((prev) => [...prev, emptyUboRow()])}
              >
                <Plus className="w-3.5 h-3.5" /> 添加
              </button>
            </div>
            {uboRows.map((row, index) => (
              <div key={row.key} className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-xs font-medium text-slate-500">受益人 {index + 1}</p>
                  {uboRows.length > 1 ? (
                    <button
                      type="button"
                      className="btn-ghost text-xs text-red-600"
                      onClick={() => setUboRows((prev) => prev.filter((r) => r.key !== row.key))}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> 删除
                    </button>
                  ) : null}
                </div>
                <label className="block">
                  <span className="label">姓名 *</span>
                  <input
                    className="input"
                    value={row.uboName}
                    onChange={(e) =>
                      setUboRows((prev) =>
                        prev.map((r) => (r.key === row.key ? { ...r, uboName: e.target.value } : r)),
                      )
                    }
                  />
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="label">国籍</span>
                    <select
                      className="input"
                      value={row.uboNationality}
                      onChange={(e) =>
                        setUboRows((prev) =>
                          prev.map((r) =>
                            r.key === row.key ? { ...r, uboNationality: e.target.value } : r,
                          ),
                        )
                      }
                    >
                      {NATIONS.map((n) => (
                        <option key={n.value} value={n.value}>
                          {n.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="label">证件类型 *</span>
                    <select
                      className="input"
                      value={row.uboIdType}
                      onChange={(e) =>
                        setUboRows((prev) =>
                          prev.map((r) => (r.key === row.key ? { ...r, uboIdType: e.target.value } : r)),
                        )
                      }
                    >
                      {ID_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="block">
                  <span className="label">证件号 *</span>
                  <input
                    className="input font-mono"
                    value={row.uboIdNo}
                    onChange={(e) =>
                      setUboRows((prev) =>
                        prev.map((r) => (r.key === row.key ? { ...r, uboIdNo: e.target.value } : r)),
                      )
                    }
                    autoComplete="off"
                  />
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="label">持股比例（%）</span>
                    <input
                      className="input"
                      value={row.ownershipPercentage}
                      onChange={(e) =>
                        setUboRows((prev) =>
                          prev.map((r) =>
                            r.key === row.key ? { ...r, ownershipPercentage: e.target.value } : r,
                          ),
                        )
                      }
                      placeholder="如 30"
                    />
                  </label>
                  <label className="block">
                    <span className="label">控制类型</span>
                    <select
                      className="input"
                      value={row.controlType}
                      onChange={(e) =>
                        setUboRows((prev) =>
                          prev.map((r) =>
                            r.key === row.key ? { ...r, controlType: e.target.value } : r,
                          ),
                        )
                      }
                    >
                      <option value="">请选择</option>
                      {CONTROL_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={row.isPep}
                    onChange={(e) =>
                      setUboRows((prev) =>
                        prev.map((r) => (r.key === row.key ? { ...r, isPep: e.target.checked } : r)),
                      )
                    }
                  />
                  政治公众人物（PEP）
                </label>
                {row.isPep ? (
                  <label className="block">
                    <span className="label">PEP 说明</span>
                    <input
                      className="input"
                      value={row.pepDetails}
                      onChange={(e) =>
                        setUboRows((prev) =>
                          prev.map((r) =>
                            r.key === row.key ? { ...r, pepDetails: e.target.value } : r,
                          ),
                        )
                      }
                    />
                  </label>
                ) : null}
                <PublicSlotUpload
                  token={token}
                  label="UBO 影像"
                  value={row.file}
                  onChange={(next) =>
                    setUboRows((prev) =>
                      prev.map((r) => (r.key === row.key ? { ...r, file: next } : r)),
                    )
                  }
                  onGone={markGone}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">提交前请核对；证件号仅掩码展示。</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <dt className="text-xs text-slate-400">法定名称</dt>
              <dd>{legalName.trim() || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">登录邮箱</dt>
              <dd>{loginEmail.trim() || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">注册号</dt>
              <dd>{registrationNo.trim() || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">营业执照</dt>
              <dd>{license ? '已上传' : '未上传'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">法人</dt>
              <dd>
                {legalPersonName.trim() || '—'} · {idTypeLabel(legalPersonIdType)} ·{' '}
                {maskSecret(legalPersonIdNo)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">法人影像</dt>
              <dd>{legalFiles.length} 张</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-slate-400">意向产品</dt>
              <dd>
                {products
                  .filter((p) => intendedProducts.includes(p.productId))
                  .map((p) => p.productName || p.productId)
                  .join('、') || '—'}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-slate-400">最终受益人</dt>
              <dd className="space-y-1">
                {uboRows.filter(isValidUboRow).map((row) => (
                  <p key={row.key}>
                    {row.uboName.trim()} · {idTypeLabel(row.uboIdType)} · {maskSecret(row.uboIdNo)}
                    {row.ownershipPercentage.trim() ? ` · ${row.ownershipPercentage.trim()}%` : ''}
                  </p>
                ))}
              </dd>
            </div>
          </dl>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button
          type="button"
          className="btn-secondary"
          disabled={step === 0 || submitting}
          onClick={() => {
            setFormError(null);
            setStep((s) => Math.max(0, s - 1));
          }}
        >
          上一步
        </button>
        {step < STEPS.length - 1 ? (
          <button type="button" className="btn-primary" onClick={goNext}>
            下一步
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary"
            disabled={submitting}
            onClick={() => void onSubmit()}
          >
            {submitting ? '提交中…' : '提交申请'}
          </button>
        )}
      </div>
    </div>,
  );
}
